import { useEffect, useState } from "react";
import { Lightbulb } from "lucide-react";
import api from "@/lib/api";
import { reportSubject } from "@/lib/subjects";

const T = {
  en: {
    error: "Reading help could not be loaded. Please refresh the page.",
    title: (n) => `Where ${n} needed reading help`,
    summary: (n, w) => `Questions where ${n} pressed "Ed mwa" for a Kreol hint${w}. Reading them together at home helps a lot.`,
    week: (k) => ` · ${k} this week`,
    empty: "No hints used yet.",
  },
  fr: {
    error: "L'aide à la lecture n'a pas pu être chargée. Merci de rafraîchir la page.",
    title: (n) => `Où ${n} a eu besoin d'aide pour lire`,
    summary: (n, w) => `Questions pour lesquelles ${n} a appuyé sur « Ed mwa » pour un indice en kreol${w}. Les relire ensemble à la maison aide beaucoup.`,
    week: (k) => ` · ${k} cette semaine`,
    empty: "Aucun indice utilisé pour l'instant.",
  },
};

// Parent view: questions where the child pressed "Ed mwa" (Kreol hint), most recent first.
export default function HelpReport({ childId, name, lang = "en" }) {
  const [data, setData] = useState(null);
  const t = T[lang] || T.en;
  const fr = lang === "fr";
  useEffect(() => {
    api.get(`/children/${childId}/help-report`).then(({ data }) => setData(data)).catch(() => setData({ error: true }));
  }, [childId]);
  if (!data) return null;
  if (data.error) return <p className="text-sm text-slate-400 mt-6" data-testid="help-report-error">{t.error}</p>;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white shadow-sm p-6 mt-6" data-testid="help-report">
      <h2 className="font-bold text-lg flex items-center gap-2"><Lightbulb size={18} className="text-amber-500" /> {t.title(name)}</h2>
      <p className="text-sm text-slate-500 mt-1" data-testid="help-report-summary">
        {t.summary(name, data.items.length ? t.week(data.this_week) : "")}
      </p>
      {data.items.length === 0 ? (
        <p className="text-sm text-slate-400 mt-4" data-testid="help-report-empty">{t.empty}</p>
      ) : (
        <ul className="mt-4 divide-y divide-slate-100">
          {data.items.map((it, k) => {
            const ct = fr ? it.chapter_title_fr || it.chapter_title : it.chapter_title;
            return (
              <li key={k} className="py-3 flex flex-col sm:flex-row sm:items-start gap-1 sm:gap-4" data-testid={`help-report-item-${k}`}>
                <span className="text-xs font-semibold text-slate-500 sm:w-48 shrink-0">
                  {reportSubject(it.subject, lang)}{ct ? ` · ${ct}` : ""}
                </span>
                <span className="flex-1 text-sm">{it.question}</span>
                <span className="text-xs text-slate-400 shrink-0">
                  {new Date(it.last_at).toLocaleDateString(fr ? "fr-FR" : undefined)}{it.count > 1 ? ` · ${it.count}×` : ""}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
